import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Eye, PenTool, RefreshCw, ShieldCheck, Monitor, LineChart, Users, MessageSquare, Briefcase, GraduationCap, MapPin } from "lucide-react";
import { useApi, useSettings, track, fmtDate, waLink } from "@/lib/api";
import { SectionHeader, ProgramCard } from "@/components/ui-hot";
import { FaqAccordion } from "@/components/FaqAccordion";

export const IMG = {
  lab: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1400&q=70",
  floor: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&w=1400&q=70",
  student: "https://images.pexels.com/photos/5527517/pexels-photo-5527517.jpeg?auto=compress&cs=tinysrgb&w=1200",
  mentor: "https://images.pexels.com/photos/6285132/pexels-photo-6285132.jpeg?auto=compress&cs=tinysrgb&w=1200",
  analytics: "https://images.pexels.com/photos/7567223/pexels-photo-7567223.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

export const TrustBar = () => {
  const { data: s } = useSettings();
  return (
    <section aria-label="Trust metrics" className="border-y border-white/10 bg-[#0B1324]">
      <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
        {(s?.trust_metrics || []).filter((m) => m.verified).map((m, i) => (
          <div key={i} data-testid={`trust-metric-${i}`} className={`py-7 px-4 md:px-8 rise rise-${i + 1}`}>
            <p className="font-display text-3xl sm:text-4xl font-bold text-slate-50 tabular-nums">{m.value}</p>
            <p className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-400">{m.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export const ProgramsGrid = ({ limit = 6 }) => {
  const { data } = useApi("courses", "/courses");
  const PRIMARY = ["stock-market", "technical-analysis", "fno", "nism", "complete-masterclass", "advanced-trading"];
  const list = PRIMARY.map((s) => (data || []).find((c) => c.slug === s)).filter(Boolean).slice(0, limit);
  return (
    <section className="section" id="programs">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeader eyebrow="Programs" title="What can I learn?" lead="Structured programs in equity markets, technical analysis, futures & options and NISM exam preparation — from beginner foundations to advanced process design." />
          <Link to="/programs" data-testid="programs-view-all" className="btn btn-outline mb-12">All Programs <ArrowRight size={16} /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{list.map((c) => <ProgramCard key={c.slug} c={c} />)}</div>
        <div className="mt-8 text-center"><Link to="/programs/compare" data-testid="programs-compare-link" className="text-sm font-semibold text-[var(--blue)] hover:underline">Compare all programs side-by-side →</Link></div>
      </div>
    </section>
  );
};

const WHY = [
  ["Practical learning", "Concepts are applied on live charts and case studies, not slides alone."], ["Mentor-led sessions", "Learn from practitioners who explain why, not just what."],
  ["Live market environment", "Our Market Lab is built around observing markets as they move."], ["Structured curriculum", "Module-by-module progression with reviews."],
  ["Multi-market education", "Equity, derivatives, commodities and more — under one roof."], ["Offline in Mohali + Online", "Attend at Sector 75 or join live online."],
  ["Community & workshops", "Alumni discussions, guest sessions and meetups."], ["NISM preparation", "Role-based guidance and mock tests."],
  ["Market tools", "TradingView, broker terminals, option data, journals."], ["Case-study based", "Real market episodes broken down step by step."],
];
export const WhyHot = () => (
  <section className="section light">
    <div className="container-x">
      <SectionHeader eyebrow="Why HOT" title="Why learn with The House of Trading?" lead="We are an education academy — not a tips service. Here is what actually differs." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {WHY.map(([h, d], i) => <div key={h} data-testid={`why-item-${i}`} className="card card-hover p-5"><span className="font-mono text-xs text-[#0284c7]">{String(i + 1).padStart(2, "0")}</span><h3 className="mt-2 text-base font-semibold text-slate-900">{h}</h3><p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d}</p></div>)}
      </div>
    </div>
  </section>
);

const LAB = ["Live market observation", "Chart analysis", "Case studies", "Market discussions", "Q&A sessions", "Strategy backtesting concepts", "Trade journaling", "Risk-management exercises"];
export const MarketLabSection = ({ full }) => (
  <section className="section relative overflow-hidden" id="market-lab">
    <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-5">
        <SectionHeader as={full ? "h1" : "h2"} eyebrow="The House Market Lab" title="Learn in an environment designed around how markets actually move." lead="Our Mohali academy is a practical market-learning environment: multiple screens, live charts and data, mentors and learners discussing what is happening — as it happens." />
        <ul className="grid grid-cols-2 gap-2.5 mb-8">{LAB.map((l, i) => <li key={l} data-testid={`market-lab-feature-${i}`} className="flex items-center gap-2 text-sm text-slate-300"><Monitor size={14} className="text-[var(--cyan)] shrink-0" />{l}</li>)}</ul>
        {!full && <Link to="/market-lab" data-testid="market-lab-tour-btn" className="btn btn-outline">Explore the Market Lab <ArrowRight size={16} /></Link>}
        <p className="mt-6 text-xs text-slate-500">The Market Lab is for learning and observation. It does not provide tips, signals or guaranteed outcomes.</p>
      </div>
      <div className="lg:col-span-7 grid grid-cols-2 gap-4">
        <img src={IMG.lab} alt="Multiple monitors showing market charts in a trading learning environment" loading="lazy" width={700} height={500} className="rounded-2xl object-cover h-56 sm:h-72 w-full col-span-2" />
        <img src={IMG.mentor} alt="Mentor explaining market data to students" loading="lazy" width={600} height={400} className="rounded-2xl object-cover h-44 sm:h-56 w-full" />
        <img src={IMG.analytics} alt="Analytics dashboard with financial charts" loading="lazy" width={600} height={400} className="rounded-2xl object-cover h-44 sm:h-56 w-full" />
      </div>
    </div>
  </section>
);

const STEPS = [[BookOpen, "Learn", "Concepts explained by experienced practitioners."], [Eye, "Observe", "Study real market behaviour."], [PenTool, "Practice", "Apply concepts through exercises and case studies."], [RefreshCw, "Review", "Analyse decisions and mistakes."], [ShieldCheck, "Build Discipline", "Develop a structured process around risk and decision-making."]];
export const Methodology = () => (
  <section className="section bg-[#0B1324] border-y border-white/5">
    <div className="container-x">
      <SectionHeader eyebrow="How we teach" title="Not just classes. A market learning environment." lead="A five-step methodology repeated across every program." />
      <ol className="grid md:grid-cols-5 gap-4">
        {STEPS.map(([Icon, h, d], i) => <li key={h} data-testid={`methodology-step-${i + 1}`} className="card card-hover p-6 relative"><span className="font-mono text-xs text-slate-500">0{i + 1}</span><Icon className="mt-4 text-[var(--cyan)]" size={22} /><h3 className="mt-4 text-lg font-semibold">{h}</h3><p className="mt-2 text-sm text-slate-400 leading-relaxed">{d}</p></li>)}
      </ol>
    </div>
  </section>
);

const PATHS = ["Trader", "Research", "Dealing / Operations", "Financial Markets", "Quantitative Analytics", "Fintech", "Content & Market Education", "Entrepreneurship", "NISM / Certification Path"];
export const CareerPaths = () => (
  <section className="section">
    <div className="container-x grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4"><SectionHeader eyebrow="Career pathways" title="Where can this skill take you?" lead="Market skills open several pathways. Course completion does not guarantee employment, income or trading profitability — it builds the skill base these paths require." /></div>
      <ul className="lg:col-span-8 grid sm:grid-cols-3 gap-3">
        {PATHS.map((p, i) => <li key={p} data-testid={`career-path-${i}`} className="card card-hover p-5 flex items-center gap-3"><Briefcase size={16} className="text-[var(--blue)] shrink-0" /><span className="font-medium text-sm">{p}</span></li>)}
      </ul>
    </div>
  </section>
);

export const NISM_SERIES = [["Series VIII", "Equity Derivatives", "Relevant for derivatives dealing and approved-user roles."], ["Series V-A", "Mutual Fund Distributors", "Required for mutual fund distribution (with AMFI registration)."], ["Series X-A", "Investment Adviser (Level 1)", "Relevant for SEBI-registered adviser roles; registration has additional norms."], ["Series VII", "Securities Operations & Risk Management", "Relevant for operations, back-office and risk roles."]];
export const NismSection = ({ full }) => (
  <section className="section light" id="nism">
    <div className="container-x">
      <SectionHeader as={full ? "h1" : "h2"} eyebrow="NISM exam preparation" title="Prepare for your next step in financial markets." lead="NISM (National Institute of Securities Markets, established by SEBI) conducts certification exams for securities-market roles. Whether a certification is required depends on the specific role and regulatory framework. We prepare you for the exam; NISM awards the certification." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {NISM_SERIES.map(([s, n, d], i) => <div key={s} data-testid={`nism-series-${i}`} className="card card-hover p-6"><GraduationCap className="text-[#0284c7]" size={22} /><p className="mt-4 font-mono text-xs text-slate-500">NISM {s}</p><h3 className="text-lg font-semibold text-slate-900">{n}</h3><p className="mt-2 text-sm text-slate-600">{d}</p></div>)}
      </div>
      {!full && <Link to="/nism" data-testid="nism-learn-more" className="btn btn-dark mt-8">NISM Preparation Program <ArrowRight size={16} /></Link>}
    </div>
  </section>
);

export const MentorCard = ({ m }) => (
  <article data-testid="mentor-profile-card" className="card card-hover p-6">
    <div className="h-56 rounded-xl bg-gradient-to-br from-[#1E3A8A]/40 to-[#131D31] grid place-items-center mb-5 overflow-hidden">{m.photo ? <img src={m.photo} alt={m.name} loading="lazy" className="h-full w-full object-cover object-top" /> : <Users className="text-slate-600" size={36} />}</div>
    <h3 className="text-lg font-semibold">{m.name}</h3>
    <p className="text-sm text-[var(--blue)]">{m.designation}</p>
    <p className="mt-2 text-xs text-slate-400">{m.specialisation}</p>
    {m.experience_years && <p className="mt-1 text-xs text-slate-400">{m.experience_years}+ years of experience</p>}
    <p className="mt-3 text-xs text-slate-500 leading-relaxed">{m.background}</p>
    {m.nism_certifications?.length > 0 && <ul className="mt-3 flex flex-wrap gap-1.5">{m.nism_certifications.map((c) => <li key={c} className="chip !text-[10px] border-[var(--blue)]/30 text-[var(--blue)]">{c}</li>)}</ul>}
    {m.is_placeholder && <span className="chip mt-3 border-amber-400/30 text-amber-300">Profile being verified</span>}
    {m.linkedin && <a href={m.linkedin} className="mt-3 block text-xs text-[var(--blue)]" target="_blank" rel="noreferrer">LinkedIn →</a>}
  </article>
);
export const MentorsSection = () => {
  const { data } = useApi("mentors", "/mentors");
  return (
    <section className="section">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6"><SectionHeader eyebrow="Mentors" title="Who will teach me?" lead="Practitioner mentors across equity, derivatives, commodities and NISM preparation. Profiles are published only after verification by the HOT team." /><Link to="/mentors" className="btn btn-outline mb-12">Meet the Mentors</Link></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{(data || []).filter((m) => !m.is_placeholder).slice(0, 3).map((m, i) => <MentorCard key={i} m={m} />)}</div>
      </div>
    </section>
  );
};

export const StoryCard = ({ t }) => (
  <blockquote data-testid="student-story-card" className="card card-hover p-6 flex flex-col">
    <MessageSquare size={18} className="text-[var(--cyan)] mb-4" />
    <p className="text-sm text-slate-200 leading-relaxed flex-1">“{t.quote}”</p>
    <footer className="mt-5 text-xs"><p className="font-semibold text-slate-100">{t.name}</p><p className="text-slate-400">{t.course} · {t.location}</p></footer>
  </blockquote>
);
export const StoriesSection = () => {
  const { data } = useApi("testimonials", "/testimonials");
  return (
    <section className="section bg-[#0B1324] border-y border-white/5">
      <div className="container-x">
        <SectionHeader eyebrow="Student stories" title="Outcome-focused, in their own words." lead="We publish only consented, verified testimonials and never use them as proof of financial outcomes." />
        <div className="grid md:grid-cols-3 gap-5">{(data || []).filter((t) => !/forex|crypto/i.test(t.course)).slice(0, 3).map((t, i) => <StoryCard key={i} t={t} />)}</div>
        <Link to="/student-stories" data-testid="stories-view-all" className="mt-8 inline-flex text-sm font-semibold text-[var(--blue)]">All student stories →</Link>
      </div>
    </section>
  );
};

export const EventCard = ({ e }) => (
  <article data-testid="event-card" className="card card-hover p-6 flex flex-col">
    <div className="flex items-center justify-between mb-3"><span className="chip">{e.type}</span>{e.sample && <span className="text-[10px] font-mono text-amber-300/80">Sample schedule</span>}</div>
    <p className="font-mono text-xs text-[var(--blue)]">{fmtDate(e.date)} · {e.time}</p>
    <h3 className="mt-2 text-lg font-semibold">{e.title}</h3>
    <p className="mt-2 text-sm text-slate-400 flex-1">{e.topic}</p>
    <dl className="mt-4 grid grid-cols-2 gap-1 text-xs text-slate-300"><dd>Venue: {e.venue}</dd><dd>Speaker: {e.speaker}</dd><dd>Seats: {e.seats}</dd><dd>Price: {e.price}</dd></dl>
    {e.status === "upcoming" && <Link to={`/contact?event=${encodeURIComponent(e.title)}`} data-testid="event-register-btn" onClick={() => track("event_register_click", { title: e.title })} className="btn btn-outline mt-5 !py-2">{e.cta || "Register"}</Link>}
  </article>
);
export const EventsSection = () => {
  const { data } = useApi("events", "/events");
  const up = (data || []).filter((e) => e.status === "upcoming").slice(0, 3);
  if (!up.length) return null;
  return (
    <section className="section">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6"><SectionHeader eyebrow="Events" title="Upcoming masterclasses & free sessions" lead="Experience how we teach before you commit to a full program." /><Link to="/events" className="btn btn-outline mb-12">All Events</Link></div>
        <div className="grid md:grid-cols-3 gap-5">{up.map((e) => <EventCard key={e.id} e={e} />)}</div>
      </div>
    </section>
  );
};

export const ArticleCard = ({ a }) => (
  <Link to={`/blog/${a.slug}`} data-testid="blog-article-card" className="card card-hover p-6 flex flex-col group">
    <span className="chip w-fit">{a.category}</span>
    <h3 className="mt-4 text-lg font-semibold group-hover:text-[var(--blue)] transition-colors">{a.title}</h3>
    <p className="mt-2 text-sm text-slate-400 flex-1">{a.meta_description}</p>
    <p className="mt-4 font-mono text-xs text-slate-500">{a.reading_time} min read · {fmtDate(a.published_at)}</p>
  </Link>
);
export const HubSection = () => {
  const { data } = useApi("articles", "/articles");
  return (
    <section className="section light">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6"><SectionHeader eyebrow="Market Learning Hub" title="Learn something today." lead="Educational articles on markets, options, risk, psychology, NISM and careers." /><Link to="/blog" className="btn btn-dark mb-12">Visit the Hub</Link></div>
        <div className="grid md:grid-cols-3 gap-5">{(data || []).slice(0, 3).map((a) => <ArticleCard key={a.slug} a={a} />)}</div>
      </div>
    </section>
  );
};

export const FaqSection = ({ limit = 8 }) => {
  const { data } = useApi("faqs", "/faqs");
  return (
    <section className="section">
      <div className="container-x grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><SectionHeader eyebrow="FAQ" title="Questions students ask us." /><Link to="/faq" data-testid="faq-view-all" className="btn btn-outline">All FAQs</Link></div>
        <div className="lg:col-span-8"><FaqAccordion faqs={(data || []).slice(0, limit)} /></div>
      </div>
    </section>
  );
};

export const CounsellingCTA = () => {
  const { data: s } = useSettings();
  return (
    <section className="section bg-[#0B1324] border-y border-white/5">
      <div className="container-x card p-8 sm:p-12 grid lg:grid-cols-12 gap-8 items-center !bg-gradient-to-br !from-[#131D31] !to-[#1E3A8A]/40">
        <div className="lg:col-span-8"><p className="eyebrow mb-3">Not sure where to start?</p><h2 className="h2">Book a free counselling session.</h2><p className="lead mt-4">Talk to our admissions team about your level, goals and schedule. This is course guidance — not investment advice.</p></div>
        <div className="lg:col-span-4 flex flex-col gap-3">
          <Link to="/book-counselling" data-testid="cta-book-counselling" className="btn btn-primary" onClick={() => track("cta_click", { cta: "counselling_section" })}>Book a Free Counselling Session</Link>
          <a href={waLink(s)} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="btn btn-outline" onClick={() => track("whatsapp_click", { placement: "counselling_section" })}>WhatsApp Admissions</a>
        </div>
      </div>
    </section>
  );
};

export const LocationSection = () => {
  const { data: s } = useSettings();
  return (
    <section className="section" id="location">
      <div className="container-x grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <SectionHeader eyebrow="Visit us" title="Our academy in Mohali" lead="Accessible from Chandigarh, Panchkula, Kharar, Zirakpur and across Tricity." />
          <address data-testid="location-address" className="not-italic text-sm text-slate-300 space-y-2">
            <p className="flex gap-2"><MapPin size={16} className="text-[var(--blue)] mt-0.5" /><span>{s?.address_line1},<br />{s?.address_line2}</span></p>
            <p>{s?.phone} · {s?.email}</p><p className="text-slate-400">{s?.office_hours}</p>
          </address>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={s?.google_maps_link} target="_blank" rel="noreferrer" data-testid="location-directions" className="btn btn-outline !py-2">Get Directions</a>
            {s?.google_business_profile && <a href={s.google_business_profile} target="_blank" rel="noreferrer" className="btn btn-outline !py-2">Google Business Profile</a>}
          </div>
        </div>
        <div className="lg:col-span-7 card overflow-hidden" data-testid="location-map-card">
          {s?.google_maps_embed && <iframe title="The House of Trading location map" src={s.google_maps_embed} width="100%" height="380" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="block grayscale-[30%] contrast-[1.05]" allowFullScreen />}
        </div>
      </div>
    </section>
  );
};

export const LineArt = () => <LineChart className="text-[var(--cyan)]" />;
